import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { completeLinkedTasks } from '@/src/lib/taskLinks';

export interface TrainingCourse {
  id: string;
  venue_id: string;
  title: string;
  description: string | null;
  category: string | null;
  required_for_roles: string[] | null;
  active: boolean;
  created_at: string;
  /** learning: explanation after each question, unlimited tries. test: one attempt. */
  quiz_mode?: QuizMode | null;
}

export type QuizMode = 'learning' | 'test';
export const modeOf = (c?: { quiz_mode?: QuizMode | null } | null): QuizMode => (c?.quiz_mode === 'test' ? 'test' : 'learning');
const MODE_MIGRATION = 'Quiz types need the latest database update (20261028010000_quiz_modes.sql).';
const missingCol = (m?: string) => !!m && /quiz_mode|started_at|schema cache|column/i.test(m);

export interface TrainingModule {
  id: string;
  course_id: string;
  venue_id: string;
  position: number;
  title: string;
  content_type: string;
  content_url: string | null;
  content_text: string | null;
  quiz: QuizQuestion[] | null;
  pass_threshold: number;
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correct: number;
  /** Why the right answer is right, from the source document. */
  explanation?: string;
}

export interface TrainingAssignment {
  id: string;
  venue_id: string;
  user_id: string;
  course_id: string;
  assigned_at: string;
  due_at: string | null;
  completed_at: string | null;
  score: number | null;
  attempts: number;
  /** When a test was opened. A started test can't be restarted without a manager. */
  started_at?: string | null;
}

export function useTraining(venueId: string | null, userId: string | null) {
  const [courses, setCourses] = useState<TrainingCourse[]>([]);
  const [assignments, setAssignments] = useState<TrainingAssignment[]>([]);
  const [allAssignments, setAllAssignments] = useState<TrainingAssignment[]>([]);
  const [loading, setLoading] = useState(true);

  const loadCourses = useCallback(async () => {
    if (!venueId) return;
    let q = supabase
      .from('training_courses')
      .select('*')
      .eq('venue_id', venueId)
      .eq('active', true)
      .order('created_at', { ascending: false });
    const { data } = await q;
    setCourses((data as TrainingCourse[]) ?? []);
  }, [venueId]);

  const loadAssignments = useCallback(async () => {
    if (!venueId || !userId) return;
    const { data } = await supabase
      .from('training_assignments')
      .select('*')
      .eq('venue_id', venueId)
      .eq('user_id', userId);
    setAssignments((data as TrainingAssignment[]) ?? []);
  }, [venueId, userId]);

  const loadAllAssignments = useCallback(async () => {
    if (!venueId) return;
    const { data } = await supabase
      .from('training_assignments')
      .select('*')
      .eq('venue_id', venueId)
      .order('completed_at', { ascending: false });
    setAllAssignments((data as TrainingAssignment[]) ?? []);
  }, [venueId]);

  useEffect(() => {
    if (!venueId || !userId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([loadCourses(), loadAssignments(), loadAllAssignments()]).then(() => setLoading(false));
  }, [venueId, userId, loadCourses, loadAssignments, loadAllAssignments]);

  const getModules = useCallback(async (courseId: string): Promise<TrainingModule[]> => {
    const { data } = await supabase
      .from('training_modules')
      .select('*')
      .eq('course_id', courseId)
      .order('position', { ascending: true });
    return (data as TrainingModule[]) ?? [];
  }, []);

  const createCourse = useCallback(async (input: {
    title: string;
    description?: string;
    category?: string;
    required_for_roles?: string[];
    quiz_mode?: QuizMode;
  }) => {
    if (!venueId) return { error: 'no venue', data: null };
    let { data, error } = await supabase
      .from('training_courses')
      .insert({ ...input, venue_id: venueId, active: true })
      .select()
      .single();
    if (error && input.quiz_mode && missingCol(error.message)) {
      if (input.quiz_mode === 'test') return { error: MODE_MIGRATION, data: null };
      const { quiz_mode: _m, ...rest } = input; void _m;
      ({ data, error } = await supabase.from('training_courses').insert({ ...rest, venue_id: venueId, active: true }).select().single());
    }
    if (!error) await loadCourses();
    return { error: error?.message ?? null, data };
  }, [venueId, loadCourses]);

  const saveModule = useCallback(async (input: {
    course_id: string;
    title: string;
    content_text?: string;
    quiz?: QuizQuestion[];
    pass_threshold?: number;
    position?: number;
  }) => {
    if (!venueId) return { error: 'no venue' };
    const { error } = await supabase
      .from('training_modules')
      .insert({
        ...input,
        venue_id: venueId,
        content_type: 'text',
        position: input.position ?? 1,
        pass_threshold: input.pass_threshold ?? 70,
      });
    return { error: error?.message ?? null };
  }, [venueId]);

  const assignCourse = useCallback(async (courseId: string, targetUserId: string, dueAt?: string) => {
    if (!venueId) return { error: 'no venue' };
    const { error } = await supabase
      .from('training_assignments')
      .insert({ venue_id: venueId, user_id: targetUserId, course_id: courseId, due_at: dueAt ?? null });
    if (!error) await loadAllAssignments();
    return { error: error?.message ?? null };
  }, [venueId, loadAllAssignments]);

  const submitQuiz = useCallback(async (courseId: string, score: number, answers?: number[], mode: QuizMode = 'learning') => {
    if (!venueId || !userId) return { error: 'no venue/user' };
    
    // Always fetch fresh from DB
    const { data: existing } = await supabase
      .from('training_assignments')
      .select('id, attempts, score, completed_at')
      .eq('venue_id', venueId)
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .maybeSingle();
  
    // last_answers needs the quiz-feedback database update; retry without it if that hasn't been run.
    // Learning quizzes can be practised again: keep the best score so practice never lowers it.
    if (mode === 'learning' && existing?.completed_at && existing.score != null) score = Math.max(score, existing.score);
    const withAnswers = answers ? { last_answers: answers } : {};
    const run = async (extra: Record<string, unknown>) => existing
      ? supabase.from('training_assignments').update({ score, completed_at: new Date().toISOString(), attempts: (existing.attempts ?? 0) + 1, ...extra }).eq('id', existing.id)
      : supabase.from('training_assignments').insert({ venue_id: venueId, user_id: userId, course_id: courseId, score, completed_at: new Date().toISOString(), attempts: 1, ...extra });
    let { error } = await run(withAnswers);
    if (error && /last_answers/.test(error.message)) ({ error } = await run({}));
    // Passing ticks off any task that pointed this person at this quiz.
    if (!error && score >= 70) await completeLinkedTasks(userId, { type: 'quiz', course_id: courseId });
    if (!error) await loadAssignments();
    return { error: error?.message ?? null };
  }, [venueId, userId, loadAssignments]);

  /** Mark a test as started. Fails if it was already started or finished (one attempt). */
  const startTest = useCallback(async (courseId: string) => {
    if (!venueId || !userId) return { error: 'no venue/user' };
    const { data: a } = await supabase.from('training_assignments').select('id, started_at, completed_at')
      .eq('venue_id', venueId).eq('user_id', userId).eq('course_id', courseId).maybeSingle();
    if (a?.completed_at || a?.started_at) return { error: 'You have already taken this test. Ask your manager if you need to sit it again.' };
    const { error } = a
      ? await supabase.from('training_assignments').update({ started_at: new Date().toISOString() }).eq('id', a.id)
      : await supabase.from('training_assignments').insert({ venue_id: venueId, user_id: userId, course_id: courseId, started_at: new Date().toISOString(), attempts: 0 });
    if (error) return { error: missingCol(error.message) ? MODE_MIGRATION : error.message };
    await loadAssignments();
    return { error: null };
  }, [venueId, userId, loadAssignments]);

  /** Managers: switch a course between learning and test. */
  const setCourseMode = useCallback(async (courseId: string, quiz_mode: QuizMode) => {
    const { error } = await supabase.from('training_courses').update({ quiz_mode }).eq('id', courseId);
    if (!error) await loadCourses();
    return { error: error ? (missingCol(error.message) ? MODE_MIGRATION : error.message) : null };
  }, [loadCourses]);

  // Managers: replace a quiz's questions (edit, add, remove, reorder).
  const updateModuleQuiz = useCallback(async (moduleId: string, quiz: QuizQuestion[]) => {
    const { error } = await supabase.from('training_modules').update({ quiz }).eq('id', moduleId);
    return { error: error?.message ?? null };
  }, []);

  const resetStaffResults = useCallback(async (targetUserId: string) => {
    if (!venueId) return { error: 'no venue' };
    let { error } = await supabase
      .from('training_assignments')
      .update({ score: null, completed_at: null, attempts: 0, started_at: null })
      .eq('venue_id', venueId)
      .eq('user_id', targetUserId);
    if (error && missingCol(error.message)) ({ error } = await supabase.from('training_assignments').update({ score: null, completed_at: null, attempts: 0 }).eq('venue_id', venueId).eq('user_id', targetUserId));
    if (!error) await loadAllAssignments();
    return { error: error?.message ?? null };
  }, [venueId, loadAllAssignments]);

  const deleteCourse = useCallback(async (courseId: string) => {
    if (!venueId) return { error: 'no venue' };
    // Delete assignments first
    await supabase.from('training_assignments').delete().eq('course_id', courseId);
    // Then soft-delete the course
    const { error } = await supabase
      .from('training_courses')
      .update({ active: false })
      .eq('id', courseId);
    if (!error) await loadCourses();
    return { error: error?.message ?? null };
  }, [venueId, loadCourses]);

  return {
    courses,
    assignments,
    allAssignments,
    loading,
    getModules,
    createCourse,
    saveModule,
    assignCourse,
    submitQuiz,
    startTest,
    setCourseMode,
    updateModuleQuiz,
    deleteCourse,
    resetStaffResults,
    reload: () => Promise.all([loadCourses(), loadAssignments(), loadAllAssignments()]),
  };
}